import React, { useEffect, useMemo, useState } from 'react'
import { fmt } from './columnasPrecios'
import { precioConBono } from './bonosUtils'

/**
 * Vista previa del bono como lo pinta el sitio (docs/preview-bonos.png). Cada parte sale del mismo campo
 * que entrega la API y que usa el sitio (docs/arquitectura endpoint.jpg):
 *   ① precio con descuento y «Antes» tachado  -> monto + efecto (solo tacha con efecto = tacha)
 *   ② etiqueta junto al precio                 -> cobertura_tipo / cobertura
 *   ③ título de la promo                       -> nombre_promo (el sitio lo pasa a caja normal)
 *   ④ línea gris bajo el título                -> beneficio (detalle de beneficio)
 *   ⑤ etiqueta amarilla de zona                -> cobertura («APLICA EN:» fuera, resumida)
 *   ⑦ ver términos y condiciones               -> legal
 *   ⑧ cinta amarilla sobre el precio           -> tag_vtex (etiqueta promoción), solo si tiene valor
 * «Precio desde» y los botones de año vienen de VTEX en el sitio; aquí salen de los vehículos del bono.
 *
 * filas: las motos del bono [{ vehiculo, anio, pvp_actual, pvp_sap, precio_antes, cobertura }].
 */

const cajaNormal = (t) => String(t || '').toLowerCase().replace(/(^|\s)(\S)/g, (_m, a, b) => a + b.toUpperCase())
  .replace(/\bProgreser\b/g, 'ProgreSER')

/** «APLICA EN: QUINDÍO, RISARALDA, CALDAS» -> «SOLO QUINDÍO +2»; nacional -> «NACIONAL». */
const zonaCorta = (cobertura, tipo) => {
  const t = String(cobertura || '').trim()
  if (tipo === 'nacional' || /nacional/i.test(t) || !t) return 'NACIONAL'
  const partes = t.replace(/^aplica\s+(en|a)\s*:?\s*/i, '').split(/\s*(?:,|\by\b)\s*/i).map((x) => x.trim()).filter(Boolean)
  if (!partes.length) return t.toUpperCase()
  return `SOLO ${partes[0].toUpperCase()}${partes.length > 1 ? ` +${partes.length - 1}` : ''}`
}

const num = (v) => Number(String(v || '').replace(/\D/g, '')) || 0

const Pin = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ flex: 'none' }}>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
)

const PreviewBono = ({
  filas = [], nombre = '', beneficio = '', efecto = '', monto = 0, financiera = '', coberturaTipo = '',
  etiqueta = '', legal = '', esEstructural = false,
}) => {
  // Varios vehículos: el sitio muestra una ficha por moto, así que se elige cuál previsualizar.
  const vehiculos = useMemo(() => {
    const vistos = []
    filas.forEach((f) => {
      const v = f.vehiculo || f.etiqueta || ''
      if (v && !vistos.some((x) => x.nombre === v)) vistos.push({ nombre: v, conPvp: false })
      const it = vistos.find((x) => x.nombre === v)
      if (it && Number(f.pvp_actual) > 0) it.conPvp = true
    })
    return vistos
  }, [filas])
  const [veh, setVeh] = useState('')
  useEffect(() => {
    if (!vehiculos.some((v) => v.nombre === veh)) {
      // Por defecto, el primero que tenga precio (para ver el precio); si ninguno, el primero.
      setVeh(((vehiculos.find((v) => v.conPvp) || vehiculos[0]) || {}).nombre || '')
    }
  }, [vehiculos, veh])
  const delVeh = useMemo(() => {
    const d = filas.filter((f) => (f.vehiculo || f.etiqueta || '') === veh)
    return d.length ? d : filas
  }, [filas, veh])

  const anios = useMemo(() => [...new Set(delVeh.map((f) => f.anio).filter(Boolean))].sort((a, b) => a - b), [delVeh])
  const [anio, setAnio] = useState(0)
  useEffect(() => { if (!anios.includes(anio)) setAnio(anios[anios.length - 1] || 0) }, [anios, anio])
  const [verLegal, setVerLegal] = useState(false)

  // La moto que se previsualiza: la del vehículo y año elegidos (con precio si alguna lo tiene).
  const delAnio = delVeh.filter((f) => f.anio === anio)
  const moto = delAnio.find((f) => Number(f.pvp_actual) > 0) || delAnio[0] || delVeh[0] || null
  // F95: los mismos `precio` y `precio_con_bono` que entrega la API.
  const base = moto ? Number(moto.pvp_actual || 0) : 0
  const conBono = moto
    ? precioConBono({ precio: base, sap: moto.pvp_sap, efecto, monto, estructural: esEstructural })
    : null
  const cobertura = (moto && moto.cobertura) || (coberturaTipo === 'nacional' ? 'Nacional' : '')
  const tacha = efecto === 'tacha'

  // ① Precio grande y «Antes»: estructural = antes (se escribe) y ahora (SAP); comercial = PVP y PVP - monto.
  let antes = 0
  let ahora = base
  if (tacha && esEstructural) {
    antes = num(moto && moto.precio_antes)
    ahora = conBono || 0
  } else if (tacha) {
    antes = base
    ahora = conBono || 0
  }
  const sinMedio = !financiera || /medios/i.test(financiera)
  const lineaPago = sinMedio ? 'Con cualquier medio de pago' : `Financiando con ${cajaNormal(financiera)}`

  // ④ La línea gris: el detalle de beneficio; si no hay, lo que el sitio escribe según el efecto.
  const lineaBeneficio = beneficio
    || (efecto === 'precio_financiera' && conBono ? `Precio con esta financiera: ${fmt(conBono)}` : '')
    || (tacha ? 'El Dcto ya está aplicado al precio arriba.' : '')

  const s = {
    tarjeta: { border: '1px solid #dfe3e8', borderRadius: 14, background: '#fff', overflow: 'hidden', width: 300, fontFamily: 'inherit' },
    pill: { display: 'inline-flex', alignItems: 'center', gap: 4, border: '1.5px solid #111', borderRadius: 14, padding: '2px 9px', fontSize: 10.5, fontWeight: 800, color: '#111' },
    anio: (on) => ({ border: `1px solid ${on ? '#111827' : '#d6dbe1'}`, background: on ? '#111827' : '#fff', color: on ? '#fff' : '#111', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }),
  }

  return (
    <div>
      {vehiculos.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, maxWidth: 300 }}>
          {vehiculos.map((v) => (
            <button
              type="button"
              key={v.nombre}
              onClick={() => setVeh(v.nombre)}
              title={v.conPvp ? 'Ver la ficha de este vehículo' : 'Este vehículo no tiene PVP en Lucia'}
              style={{
                border: `1px solid ${veh === v.nombre ? '#0f2439' : '#d6dbe1'}`,
                background: veh === v.nombre ? '#0f2439' : '#fff',
                color: veh === v.nombre ? '#fff' : '#0f2439',
                borderRadius: 14, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {v.nombre}{v.conPvp ? '' : ' · sin PVP'}
            </button>
          ))}
        </div>
      )}
      <div style={s.tarjeta}>
        <div style={{ padding: '14px 16px 12px' }}>
          {/* ⑧ tag_vtex (etiqueta promoción): solo si tiene valor, como cinta sobre el precio. */}
          {String(etiqueta || '').trim() && (
            <span
              title="tag_vtex (Etiqueta promoción)"
              style={{
                display: 'inline-block', marginBottom: 8, border: '1.5px solid #111', borderRadius: 14, padding: '2px 12px',
                fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, color: '#111', background: '#f6c445',
              }}
            >
              {String(etiqueta).trim()}
            </span>
          )}
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Precio desde</div>
          {anios.length > 0 && (
            <div style={{ display: 'flex', gap: 8, margin: '8px 0 10px' }}>
              {anios.map((a) => (
                <button type="button" key={a} style={s.anio(a === anio)} onClick={() => setAnio(a)}>{a}</button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              {tacha && antes > 0 && (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Antes</div>
                  <div style={{ fontSize: 13, color: '#e11d48', textDecoration: 'line-through' }}>{fmt(antes)}</div>
                </>
              )}
            </div>
            {/* ② Etiqueta junto al precio: la cobertura del bono que tacha. */}
            {tacha && <span style={s.pill}><Pin />{coberturaTipo === 'regional' ? 'REGIONAL' : 'NACIONAL'}</span>}
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#111', lineHeight: 1.15 }}>
            {ahora > 0 ? fmt(ahora) : <span style={{ color: '#c9ced4', fontSize: 18 }}>Sin precio de la moto</span>}
          </div>
          <div style={{ fontSize: 12, color: '#111', marginTop: 2 }}>{lineaPago}</div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 12px 8px' }}>
          <div style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, color: '#111', marginBottom: 8 }}>
            MIRA AQUÍ TODOS LOS BENEFICIOS
          </div>
          <div style={{ background: '#f1f3f7', borderRadius: 10, padding: '10px 14px' }}>
            {/* ③ título · ④ línea gris · ⑤ etiqueta amarilla */}
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{nombre ? cajaNormal(nombre) : <span style={{ color: '#c9ced4' }}>Nombre de la promoción</span>}</div>
            {lineaBeneficio && <div style={{ fontSize: 11.5, color: '#4b5563', marginTop: 2 }}>{lineaBeneficio}</div>}
            <span
              title={cobertura}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f6c445', borderRadius: 12, padding: '3px 10px', fontSize: 10.5, fontWeight: 800, color: '#111', marginTop: 6 }}
            >
              <Pin />{zonaCorta(cobertura, coberturaTipo)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 8 }}>
            <span style={{ width: 22, height: 5, borderRadius: 3, background: '#111' }} />
            {[0, 1, 2].map((i) => <span key={i} style={{ width: 5, height: 5, borderRadius: 3, background: '#d1d5db' }} />)}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 12px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => setVerLegal((v) => !v)}
            style={{ background: 'none', border: 'none', fontSize: 13, fontWeight: 600, color: '#111', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Ver términos y condiciones
          </button>
          {verLegal && (
            <div style={{ textAlign: 'left', fontSize: 11, color: '#4b5563', marginTop: 6, maxHeight: 160, overflow: 'auto' }}>
              {legal || <span style={{ color: '#d64545' }}>Sin términos: el sitio mostraría el modal vacío.</span>}
            </div>
          )}
        </div>
      </div>

      {/* ⑧ La etiqueta ahora va dentro de la ficha (arriba). Sin etiqueta, se avisa aquí. */}
      {!String(etiqueta || '').trim() && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#9aa5b1' }}>Sin etiqueta promoción (tag_vtex vacío).</div>
      )}
      {moto && <div style={{ marginTop: 4, fontSize: 11, color: '#9aa5b1' }}>Vista con {moto.vehiculo || moto.etiqueta} {moto.anio || ''}</div>}
      {moto && !base && (
        <div style={{ marginTop: 2, fontSize: 11, color: '#d64545' }}>
          Esta moto no tiene PVP en Lucia: la API entrega precio y precio_con_bono vacíos.
        </div>
      )}
    </div>
  )
}

export default PreviewBono
